import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-sm font-semibold transition-colors ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-iris-strong ' +
    'focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian ' +
    'disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // The brand green is the single point of focus — the primary action.
        primary: 'bg-iris text-on-iris hover:bg-iris-strong',
        outline:
          'border border-border bg-transparent text-bone hover:bg-surface-2 hover:border-border-strong',
        ghost: 'border border-transparent text-haze hover:bg-surface-2 hover:text-bone',
        danger: 'border border-transparent text-danger hover:bg-danger-dim',
      },
      size: {
        default: 'h-9 px-4 text-sm',
        sm: 'h-8 px-3 text-xs',
      },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />
  ),
);
Button.displayName = 'Button';
